import { PortableText } from '@portabletext/react'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  DSButton,
  DSModal,
  DSTooltip,
  TAB_TYPE,
  Tab,
  useToast,
} from '@repo/design-system/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { differenceInDays, differenceInMilliseconds, format, isPast, parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import Slider from 'react-slick'
import Cookies from 'universal-cookie'
import apis from '../../../modules/apis'
import { axiosGet, axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import LinkLocale from '../../../modules/i18n/LinkLocale'
import { n_common, n_content, n_event, n_rwm, s_signin, s_webinar } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { buildSanityImage } from '../../../modules/utils'
import { COOKIE_LIST, COOKIE_OPT } from '../../../modules/vars'
import { EVENT_DETAIL_BODY_QUERY } from '../../../sanity/groqQueries'
import { EVENT_DETAIL_BODY_QUERYResult } from '../../../sanity/types'
import { convertToEmbedUrl, ptComponents, updateMarksToBreak } from '../../sanity/util'
import { isWebinar } from '../study/ContentProgramBody'
import EventCard from './EventCard'
import { IEventInitialData } from './type'
const fetchDetailData = async (slug: string | string[]): Promise<any> => {
  try {
    const params: Record<string, any> = {
      slug: slug,
    }

    const data: EVENT_DETAIL_BODY_QUERYResult = await client.fetch(EVENT_DETAIL_BODY_QUERY, params)
    return data
  } catch (error) {
    console.error('Failed to fetch initial data:', error)
    throw error
  }
}

const MainImage = ({ image }) => {
  return (
    <div className="w-full lg:w-auto">
      <Image
        alt={image.alt}
        loading="lazy"
        className="mx-auto object-cover"
        src={buildSanityImage(image).url()}
        style={{
          width: '100%',
          height: '100%',
        }}
        width={688}
        height={387}
        quality={100}
      />
    </div>
  )
}

const MainBodySection = ({ slug, initialData }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [activeTabIdx, setActiveTabIdx] = useState(0)
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0)

  const { query } = useRouter()

  const detailSlug = slug ? slug : query.slug //todo fix me to isWebview
  const { data: detailData, isFetching } = useQuery({
    queryKey: ['detail', detailSlug],
    queryFn: () => fetchDetailData(detailSlug),
  })
  console.log(detailData, 'detailData')

  return (
    <div className="relative">
      <div className="sticky top-[0px] z-10 h-[52px] bg-white sm:top-[60px]">
        <Tab
          className="mx-[16px] h-[52px] lg:mx-[0px]"
          type={TAB_TYPE.SCROLL}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          <Tab.TabItem
            name={
              <a href={'#Information'} className="Body1-16Bd">
                {initialData.eventType === EVENT_TYPE.LIVE
                  ? t(n_event.detail_menu.title_1)
                  : t(n_event.detail_menu.title_2)}
              </a>
            }
          ></Tab.TabItem>
          <Tab.TabItem
            name={
              <a href={'#FAQ'} className="Body1-16Bd">
                {t(n_event.detail_menu.title_3)}
              </a>
            }
          ></Tab.TabItem>
          {detailData?.recommendedContentsArray && detailData?.recommendedContentsArray.length > 0 ? (
            <Tab.TabItem
              name={
                <a href={'#Contents'} className="Body1-16Bd">
                  {t(n_event.detail_menu.title_4)}
                </a>
              }
            ></Tab.TabItem>
          ) : (
            <></>
          )}
        </Tab>
      </div>

      {initialData.eventType === EVENT_TYPE.REPLAY && !isFetching && detailData?.replayLink && (
        <div className="px-[16px]">
          <div className="Title1-20Bd mt-[14px] mb-[16px]">{t(n_event.card.label_5)}</div>
          <div className="aspect-[16/9]">
            <iframe
              width="100%"
              height="100%"
              className="aspect-[16/9]"
              src={convertToEmbedUrl(detailData?.replayLink)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {isFetching ? (
        <></>
      ) : (
        <div className="relative mb-[48px] flex flex-col">
          <div className="lists-class px-[16px] py-[24px]" id="Information">
            <PortableText value={updateMarksToBreak(detailData.information.content)} components={ptComponents} />
          </div>
          <hr className="mx-[16px] mb-[24px]" />
          <div className="Title1-20Bd mb-[16px] px-[16px]" id="FAQ">
            {t(n_common.label_faq)}
          </div>
          <div className="lists-class px-[16px]">
            <PortableText value={updateMarksToBreak(detailData.faq.content)} components={ptComponents} />
          </div>
        </div>
      )}
      {detailData?.recommendedContentsArray && detailData?.recommendedContentsArray.length > 0 && (
        <div className="rounded-[8px] bg-gray-50 p-[16px]">
          <div className="Title1-20Bd mb-[16px]" id="Contents">
            {t(n_event.detail_menu.title_4)}
          </div>
          <div className="relative pb-[20px]">
            <div className="evaluation-slider no-scrollbar mt-[16px] h-[470px] w-full overflow-x-hidden  overflow-y-hidden lg:w-[656px] ">
              <div className="overlfow-x-hidden ">
                <Slider
                  speed={300}
                  infinite={true}
                  cssEase="linear"
                  dots={true}
                  slidesToShow={1}
                  swipeToSlide={true}
                  centerMode={false}
                  variableWidth={true}
                  className="evaluation-slider overlfow-x-hidden bg-gray-50"
                  afterChange={(index) => setActiveItemIndex(index)}
                  customPaging={function (i = Number(activeItemIndex)) {
                    return (
                      <div
                        className={`mx-auto mt-10 h-6 rounded-full bg-gray-200 ${
                          i == activeItemIndex ? `w-[12px] bg-gray-800` : 'w-[6px] bg-gray-200'
                        }`}
                        onClick={() => setActiveItemIndex(i)}
                      ></div>
                    )
                  }}
                >
                  {detailData?.recommendedContentsArray?.map((postItem, i) => {
                    return (
                      <div className="max-w-[326px] pr-[16px]" key={postItem._id}>
                        <EventCard
                          title={postItem.title}
                          thumbnail={postItem.thumbnail}
                          tagLists={postItem.tags}
                          dueDate={postItem.dueDate}
                          startDate={postItem.startDate}
                          isDetailPage={true}
                          eventType={postItem.eventType}
                          isOnGoing={postItem.isOnGoingEvent}
                          onClickCard={() => router.push(urls.student.newPortal.event.detail(postItem.slug.current))}
                          onClickTag={(tag) => router.push(urls.student.newPortal.event.tag(tag))}
                        />
                      </div>
                    )
                  })}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const EVENT_TYPE = {
  EVENT: 'Event',
  LIVE: 'LIVE',
  REPLAY: 'Replay',
  PROMOTION: 'Promotion',
}

export const getStatusBadge = (t, eventType) => {
  switch (eventType) {
    case EVENT_TYPE.EVENT:
      return (
        <div className="Caption-12Bd rounded-[4px]  bg-purple-50 px-[6px] pt-[2px] text-purple-600">
          {t(n_event.card.label_2)}
        </div>
      )
    case EVENT_TYPE.LIVE:
      return (
        <div className="Caption-12Bd rounded-[4px] bg-purple-400 px-[6px] pt-[2px] text-white">
          {t(n_event.card.label_1)}
        </div>
      )
    case EVENT_TYPE.REPLAY:
      return (
        <div className="Caption-12Bd rounded-[4px] bg-gray-600 px-[6px] pt-[2px] text-white">
          {t(n_event.list.menu_3)}
        </div>
      )
    case EVENT_TYPE.PROMOTION:
      return (
        <div className="Caption-12Bd rounded-[4px]  bg-purple-50 px-[6px] pt-[2px] text-purple-600">
          {t(n_event.list.menu_6)}
        </div>
      )
    default:
      return (
        <div className="Caption-12Bd rounded-[4px]  bg-purple-50 px-[6px] pt-[2px] text-purple-600">
          {t(n_event.card.label_2)}
        </div>
      )
  }
}

export const getApplicationStatus = (t, deadline) => {
  const now = new Date()
  const targetDate = typeof deadline === 'string' ? new Date(deadline) : deadline
  const parsedDate1 = parseISO(format(new Date(), 'yyyy-MM-dd'))
  const parsedDate2 = parseISO(format(targetDate, 'yyyy-MM-dd'))

  const daysDifference = differenceInDays(parsedDate2, parsedDate1)

  if (daysDifference > 5) {
    return (
      <div className="Caption-12Bd rounded-[4px] bg-blue-50 px-[6px] pt-[2px] text-blue-600">
        {t(n_event.card.label_9)}
      </div>
    )
  } else if (daysDifference > 1) {
    return (
      <div className="Caption-12Bd rounded-[4px] bg-red-50 px-[6px] pt-[2px] text-red-500">
        {t(n_event.card.label_8(daysDifference))}
      </div>
    )
  } else if (daysDifference === 1) {
    return (
      <div className="Caption-12Bd rounded-[4px] bg-red-50 px-[6px] pt-[2px] text-red-500">
        {t(n_event.card.label_8(daysDifference))}
      </div>
    )
  } else if (daysDifference <= 0) {
    const remainingSec = differenceInMilliseconds(targetDate, now)
    if (remainingSec < 0) {
      return (
        <div className="Caption-12Bd rounded-[4px] bg-gray-100 px-[6px] pt-[2px] text-gray-600">
          {t(n_event.card.label_6)}
        </div>
      )
    } else {
      return (
        <div className="Caption-12Bd rounded-[4px] bg-red-50 px-[6px] pt-[2px] text-red-500">
          {t(n_event.card.label_7)}
        </div>
      )
    }
  } else {
    return (
      <div className="Caption-12Bd rounded-[4px] bg-gray-100 px-[6px] pt-[2px] text-gray-600">
        {t(n_event.card.label_6)}
      </div>
    )
  }
}

const InfoSection = ({ initialData }) => {
  const {
    eventType,
    title,
    dueDate,
    startDate,
    endDate,
    tags,
    link,
    isAppliableEvent,
    _id: id,
    _type: type,
  } = initialData
  const { t } = useTranslation()
  const convertToBrowserTimezone = (utcDateString) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const zonedDate = utcToZonedTime(utcDateString, timeZone)

    const formattedDate = format(zonedDate, 'yyyy.MM.dd a hh:mm')

    return `${formattedDate}`
  }

  return (
    <div className="lg:border-1 p-[16px] lg:w-[336px] lg:rounded-[8px]">
      <div className="flex flex-row gap-[4px]">
        <div className="">{getStatusBadge(t, eventType)}</div>
        <div className="">{getApplicationStatus(t, dueDate)}</div>
      </div>
      <div className="Title2-18Bd mt-[8px]">{title}</div>
      <div className="mt-[4px]">
        <div className="Body2-14Md text-gray-500">{t(n_event.card.caption_1(convertToBrowserTimezone(dueDate)))}</div>
        <div className="Body2-14Md text-gray-500">{t(n_event.card.caption_2(convertToBrowserTimezone(startDate)))}</div>
      </div>
      <div className="mt-[16px] flex flex-row gap-[8px]">
        {tags?.map((tag, i) => {
          return (
            <LinkLocale key={i} href={urls.student.newPortal.event.tag(tag)}>
              <div className="Caption-12Bd rounded-[4px] bg-gray-100 px-[6px] text-gray-600">{tag}</div>
            </LinkLocale>
          )
        })}
      </div>
      <div className="hidden lg:block">
        {isAppliableEvent && (
          <CTAButton
            eventType={eventType}
            type={type}
            id={id}
            link={link}
            startDate={startDate}
            endDate={endDate}
            dueDate={dueDate}
          />
        )}
      </div>
    </div>
  )
}

const CTAButton = ({ eventType, type, id, link, startDate, endDate, dueDate }) => {
  const { t } = useTranslation()
  const cookies = new Cookies()
  const { toastSuccess } = useToast()
  const { currentUser } = useCurrentUser()
  const router = useAppRouter()
  const isWebview = router.asPath.includes('/webview/')
  const getFullUrl = useReturnFullUrl()

  const [enrolled, setEnrolled] = useState<boolean>(true)
  const [showLoginPopup, setShowLoginPopup] = useState<boolean>(false)

  const {
    data: enrolledData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['enrolled', id, currentUser?.id],
    queryFn: () => fetchEnrolledData(),
    enabled: !!currentUser,
  })

  const isEnded = (endDateStr) => {
    const endDate = parseISO(endDateStr)

    return isPast(endDate)
  }

  const isEntryPossible = (startDate) => {
    const now = new Date()
    const entryTime = new Date(startDate)
    entryTime.setMinutes(entryTime.getMinutes() - 15) // Set the entry time to 15 minutes before the start date

    return now >= entryTime
  }

  const handleClickApply = async () => {
    try {
      if (!currentUser) {
        setShowLoginPopup(true)
        return
      }

      if (isEnterable && enrolled) {
        isWebview ? router.linking(link.link) : window.open(link.link, '_blank')
        return
      }

      const response = await axiosPost(
        apis.contentPrograms.enroll(),
        {
          sanity_content_type: type,
          sanity_id: id,
        },
        {
          shouldThrowError: true,
        }
      )
      if (response && response.success) {
        toastSuccess(t(n_event.toast.lable_1))
        refetch()
      }
    } catch (e) {
      console.log(e)
      console.error(e.response.data?.error ?? e.message)
    }
  }

  const fetchEnrolledData = async () => {
    try {
      if (!currentUser) {
        return
      }

      const response = await axiosGet(apis.contentPrograms.enrolled(), {
        param: { sanity_content_type: type, sanity_id: id },
        shouldThrowError: true,
      })

      return response
    } catch (e) {
      console.log(e)
      console.error(e.response.data?.error ?? e.message)
    }
  }

  useEffect(() => {
    if (!enrolledData || isFetching) return

    if (enrolledData.enrolled) {
      setEnrolled(true)
    } else if (!enrolledData.enrolled) {
      setEnrolled(false)
    }
  }, [enrolledData, isFetching])

  function getButtonAttributes(isEndedEvent, isEndedDueDate, isEntryPossible, enrolled, link, isFetching, currentUser) {
    if (!currentUser) {
      return {
        label: t(n_content.rwm.btn),
        disabled: false,
      }
    }

    if (isEndedEvent) {
      return {
        label: t(n_event.button.label_1),
        disabled: true,
      }
    }

    if (enrolled) {
      return {
        label: link.link ? t(n_event.button.label_2) : t(n_event.button.label_3),
        disabled: !isEntryPossible,
      }
    } else {
      if (isEndedDueDate) {
        return {
          label: t(n_event.button.label_4),
          disabled: true,
        }
      } else {
        return {
          label: link.title ? link.title : t(n_event.button.label_5),
          disabled: isFetching,
        }
      }
    }
  }

  const { label, disabled } = getButtonAttributes(
    isEnded(endDate),
    isEnded(dueDate),
    isEntryPossible(startDate),
    enrolled,
    link,
    isFetching,
    currentUser
  )

  const [isEnterable, setIsEnterable] = useState(false)

  useEffect(() => {
    const checkActiveTime = () => {
      setIsEnterable(isEntryPossible(startDate))
    }

    const interval = setInterval(checkActiveTime, 20000)

    checkActiveTime()

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <div className="mt-[24px]">
      {eventType === EVENT_TYPE.EVENT || eventType === EVENT_TYPE.PROMOTION ? (
        <DSButton
          type={BUTTON_TYPE.SOLID}
          size={BUTTON_SIZE.LG}
          onClick={() =>
            isWebview
              ? link.appLink.includes('http')
                ? router.linking(link.appLink)
                : router.push('', {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: {
                      url: getFullUrl(link.appLink),
                    },
                  })
              : window.open(link.link, '_blank')
          }
        >
          <DSButton.Label>{link.title}</DSButton.Label>
        </DSButton>
      ) : enrolledData?.enrolled && !isEntryPossible(startDate) ? (
        <DSTooltip
          place={'top'}
          hasCloseButton
          isMultiline={false}
          triggerComponent={
            <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.LG} onClick={handleClickApply} disabled={disabled}>
              <DSButton.Label>{label}</DSButton.Label>
            </DSButton>
          }
          contentComponent={t(s_webinar.apply_float.inactive)}
        />
      ) : (
        <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.LG} onClick={handleClickApply} disabled={disabled}>
          <DSButton.Label>{label}</DSButton.Label>
        </DSButton>
      )}
      <DSModal id="TextOnlyMOneButtonPopup" isOpen={showLoginPopup} setIsOpen={setShowLoginPopup} size="lg">
        <DSModal.Title>{t(n_rwm.modal_login.title)}</DSModal.Title>
        <DSModal.Content>{t(n_rwm.modal_login.desc)}</DSModal.Content>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            type={BUTTON_TYPE.OUTLINE}
            size={BUTTON_SIZE.LG}
            onClick={() => {
              setShowLoginPopup(false)
            }}
          >
            <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
          </DSButton>
          <DSButton
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.LG}
            onClick={() => {
              setShowLoginPopup(false)
              router.push(urls.student.auth.signIn)
              cookies.set(COOKIE_LIST.LOGIN_AFTER_URL, router.asPath, COOKIE_OPT)
            }}
          >
            <DSButton.Label>{t(s_signin.log_in)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    </div>
  )
}
const EventDetailBody = ({ initialData, slug }: { initialData: IEventInitialData; slug?: string }) => {
  console.log(initialData, 'initialData')
  const isWebview = useIsWebview()
  const router = useAppRouter()

  useEffect(() => {
    if (!router) return

    if (isWebinar(router.query.slug)) {
      if (router.asPath.includes('landing/')) {
        router.push(urls.student.landing.study.readTogether.detail(router.query.slug))
      } else {
        router.push(urls.student.newPortal.study.readTogether.detail(router.query.slug))
      }
    }
  }, [router.query.slug])

  if (isWebinar(router.query.slug)) {
    return <div className=""></div>
  }
  return (
    <div className="relative w-full lg:mt-[38px]">
      <div className="relative flex flex-row gap-[16px]">
        <div className="flex w-screen flex-col lg:min-w-[688px]">
          <MainImage image={initialData.mainImage} />
          <div className="lg:hidden">
            <InfoSection initialData={initialData} />
          </div>
          <div className="max-w-[100vw]">
            <MainBodySection slug={slug} initialData={initialData} />
          </div>
        </div>
        <div className="hidden w-[336px] lg:block lg:w-[336px]">
          <InfoSection initialData={initialData} />
        </div>
      </div>
      <div
        className={classNames(
          isWebview ? 'sticky' : 'fixed right-[50%] translate-x-1/2',
          'bottom-[16px] w-full max-w-[750px] px-[16px] lg:hidden'
        )}
      >
        {initialData.isAppliableEvent && (
          <CTAButton
            eventType={initialData.eventType}
            id={initialData._id}
            type={initialData._type}
            link={initialData.link}
            startDate={initialData.startDate}
            endDate={initialData.endDate}
            dueDate={initialData.dueDate}
          />
        )}
      </div>
    </div>
  )
}

export default EventDetailBody
